/**
 * PLAN-OFFER-SYSTEMS (Seth 2026-10-29: "where would the tech see which equipment is
 * being selected as part of the package — not model or serial number — what about
 * adding multiple systems for a service plan?").
 *
 * A plan offer names the systems it covers, in the home's own words — the kind of
 * unit and the room it serves, never a model or serial — and is priced per system:
 * every extra system at the Plan Builder's existing second-system discount (Settings
 * → second_system_discount, 30%). The covered units come from the same equipment
 * record every Pegasus screen reads (ESC + the office's corrections + Pegasus units,
 * minus removed). One rule here; the office builder, the tech's wizard, the homeowner
 * page and the sale read it. FieldEdge lists "equipment on agreement" the same way;
 * ESC itself counts systems as task counts per agreement (two furnace checks + two
 * A/C checks = two systems) — this is that count, made visible before the sale.
 */
import { buildEquipmentView, installDateInfo, type ClassifiedEquip, type EquipCategory, type EquipmentRow, type EquipmentView, type EquipOverride } from "@/lib/equipment";

/** The make, read like a person says it, from ESC's mfg field — which also holds TYPE words
 *  ("GAS FURNACE", "Z DUCT", "( NO MFG )"); those are not makes and come back as null.
 *  An initialism stays ("AO Smith"). The brand table's name wins where a screen has it. */
export function makeFromMfg(raw: string | null | undefined): string | null {
  const t = (raw ?? "").trim();
  if (!t || /\d/.test(t) || t.split(/\s+/).length > 3) return null;
  if (/\b(FURNACE|DUCT|COND|COIL|CLEANER|HUMID|ROOFTOP|HEATER|MFG|UNIT|AIR|EQUIP|BOILER|PUMP|SPLIT|STORED|NONE|UNKNOWN|MISC|OTHER)\b/i.test(t)) return null;
  if (/^[^A-Za-z]*$/.test(t)) return null;
  return t === t.toUpperCase() ? t.split(/\s+/).map((w) => (w.length <= 2 ? w : w.charAt(0) + w.slice(1).toLowerCase())).join(" ") : t;
}

/** The 360's field-correction merge (model, serial, dates, the room) applied before classifying — the same record every screen reads. */
export function mergeEquipOverrides(rows: EquipmentRow[], overrides?: Map<string, EquipOverride> | null): EquipmentRow[] {
  if (!overrides || overrides.size === 0) return rows;
  return rows.map((r) => {
    const ov = overrides.get(r.counter);
    if (!ov) return r;
    return { ...r, mfg: ov.mfg ?? r.mfg, model: ov.model ?? r.model, serial: ov.serial ?? r.serial, install_date: ov.install_date ?? r.install_date, eq_type: ov.eq_type ?? r.eq_type, equip_loc: ov.equip_loc ?? r.equip_loc };
  });
}

/** Rows + overrides → the home's coverable systems (one call for every builder). A record that
 *  spans several properties (a quote with no location on a multi-property account) is scoped
 *  to the first location, the way the 360 opens — the office can move the quote to the right
 *  property; it never prices every building at once. */
export function planCoversFor(rows: EquipmentRow[] | undefined, overrides: Map<string, EquipOverride> | null | undefined, saved?: PlanOfferCover[] | null): PlanOfferCover[] {
  let scoped = rows ?? [];
  const locs = [...new Set(scoped.map((r) => (r as EquipmentRow & { locno?: string | null }).locno ?? ""))].filter(Boolean).sort();
  if (locs.length > 1) scoped = scoped.filter((r) => ((r as EquipmentRow & { locno?: string | null }).locno ?? "") === locs[0]);
  const view = buildEquipmentView(mergeEquipOverrides(scoped, overrides), undefined, overrides ?? undefined);
  return planCoversFromView(view, saved);
}

export interface PlanOfferCover {
  /** ESC counter, or "peg:<id>" for a Pegasus-native unit. */
  counter: string;
  kind: EquipCategory | string;
  /** "Furnace", "Air Conditioner", "Boiler" … */
  label: string;
  /** The room / area it serves ("Main house", "Addition"), when the record says. */
  loc: string | null;
  on: boolean;
  /** PROPOSAL-EQUIPMENT-02: the record's make / model / serial / year, so a staff row can tell
   *  twins apart (six rooftop units on one roof). Homeowner surfaces read the tiles, not these. */
  model?: string | null;
  serial?: string | null;
  brand?: string | null;
  year?: number | null;
}

const HEATING: ReadonlySet<string> = new Set(["furnace", "boiler", "heat-pump", "mini-split", "rooftop", "magic-pak", "garage-heater"]);
const COOLING: ReadonlySet<string> = new Set(["ac", "heat-pump", "mini-split", "rooftop", "magic-pak"]);
/** The kinds a plan tune-up covers — whole systems (a coil rides its A/C; a water heater is its own thing). */
const COVERABLE: ReadonlySet<string> = new Set(["furnace", "ac", "heat-pump", "mini-split", "rooftop", "magic-pak", "boiler", "air-handler", "garage-heater"]);

export const KIND_LABEL: Record<string, string> = {
  furnace: "Furnace", ac: "Air Conditioner", "heat-pump": "Heat Pump", "mini-split": "Mini-Split", rooftop: "Rooftop Unit",
  "magic-pak": "Magic-Pak", boiler: "Boiler", "air-handler": "Air Handler", "garage-heater": "Garage Heater", coil: "Coil", "water-heater": "Water Heater",
};

const cleanLoc = (s: string | null | undefined): string | null => {
  const t = (s ?? "").trim();
  if (!t) return null;
  // ESC writes "MAIN HOUSE" / "addition" / "Basement" — read like a person says it
  return t.length <= 3 ? t.toUpperCase() : t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

/** The home's coverable systems from the equipment record, keeping any earlier on/off choices by counter. */
export function planCoversFromView(view: Pick<EquipmentView, "systems" | "auxiliary">, saved?: PlanOfferCover[] | null): PlanOfferCover[] {
  const prior = new Map((saved ?? []).map((c) => [c.counter, c.on]));
  const units: ClassifiedEquip[] = [...view.systems, ...view.auxiliary].filter((u) => COVERABLE.has(u.category) && !u.replaced && !u.duplicateOf);
  return units.map((u) => ({
    counter: u.counter,
    kind: u.category,
    label: u.categoryLabel || KIND_LABEL[u.category] || "System",
    loc: cleanLoc(u.equip_loc),
    on: prior.has(u.counter) ? (prior.get(u.counter) as boolean) : true,
    model: (u.model ?? "").trim() || null,
    serial: (u.serial ?? "").trim() || null,
    brand: makeFromMfg(u.mfg),
    year: installDateInfo(u.install_date)?.year ?? null,
  }));
}

/** A staff chip's words for a covered unit: the kind and the room; the model when two units would
 *  read the same; the serial when the models match too (six Bryant rooftops on one roof come in
 *  three model pairs — the tech tells them apart by the plate; Seth 2026-10-29: "the technicians
 *  need to see serial #"). Homeowner surfaces read the tiles, not these. */
export function planCoverChipText(c: PlanOfferCover, all: PlanOfferCover[]): string {
  const baseOf = (x: PlanOfferCover) => `${String(x.kind) === "ac" ? "A/C" : x.label}${x.loc ? ` · ${x.loc}` : ""}`;
  const base = baseOf(c);
  const sameBase = all.filter((x) => baseOf(x) === base);
  if (sameBase.length <= 1) return base;
  const model = (c.model ?? "").trim();
  const serial = (c.serial ?? "").trim();
  if (!model) return serial ? `${base} · S/N ${serial}` : base;
  const sameModel = sameBase.filter((x) => (x.model ?? "").trim() === model);
  return sameModel.length > 1 && serial ? `${base} · ${model} · S/N ${serial}` : `${base} · ${model}`;
}

/** How many systems the covered units make: the larger of the heating count and the cooling count, never below 1. */
export function planSystemsCount(covers: PlanOfferCover[] | null | undefined): number {
  const on = (covers ?? []).filter((c) => c.on);
  const heat = on.filter((c) => HEATING.has(String(c.kind))).length;
  const cool = on.filter((c) => COOLING.has(String(c.kind))).length;
  return Math.max(1, heat, cool);
}

/** 1 → 1.0, 2 → 1.7, 3 → 2.4 at a 30% second-system discount (the same rule the Plan Builder prices by). */
export function planSystemsMultiplier(systems: number, secondDisc = 0.3): number {
  return 1 + (Math.max(1, systems || 1) - 1) * (1 - secondDisc);
}

/** "Furnace & A/C · Main house + Furnace & A/C · Addition" — the covered units, grouped by the room they serve. */
export function planCoversSummary(covers: PlanOfferCover[] | null | undefined): string {
  const on = (covers ?? []).filter((c) => c.on);
  if (on.length === 0) return "";
  const short = (c: PlanOfferCover) => (String(c.kind) === "ac" ? "A/C" : c.label);
  const groups = new Map<string, string[]>();
  for (const c of on) {
    const k = c.loc ?? "";
    groups.set(k, [...(groups.get(k) ?? []), short(c)]);
  }
  return [...groups.entries()]
    .map(([loc, kinds]) => `${[...new Set(kinds)].join(" & ")}${loc ? ` · ${loc}` : ""}`)
    .join(" + ");
}
